'use client';
import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Phone, Building2, ArrowRight, X, User, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import AuthLoader from '@/components/ui/AuthLoader';
import { getSavedAccounts, saveAccount, removeSavedAccount, updateLastUsed, getInitials } from '@/utils/savedAccounts';
import { getCurrentTenantContext } from '@/utils/tenantResolver';
import { getPreLoginHomeHref } from '@/utils/preLoginHome';
import { takeSignOutReason } from '@/utils/auth';
import SafeImage from '@/components/ui/SafeImage';

// Auth flow tracing — development only. Route names, session state and
// redirect decisions must never print in a production console.
const debugLog = process.env.NODE_ENV !== 'production' ? console.log : () => {};


/**
 * `?school=CODE` / `?schoolCode=CODE` if the current URL carries one, else ''.
 * classifyHost honours that param on any host, so it is how a school is
 * selected on localhost and in the kaympus.com preview.
 */
const schoolPreviewQuery = () => {
  if (typeof window === 'undefined') return '';
  const p = new URLSearchParams(window.location.search);
  const code = p.get('school') || p.get('schoolCode');
  return code ? `?school=${encodeURIComponent(code)}` : '';
};

/**
 * @param {{initialBrand?: {logoUrl: string, schoolName: string, schoolCode: string} | null}} props
 *   initialBrand is resolved SERVER-side by the route (app/auth/sign-in/page.jsx)
 *   so the first paint already carries the school's logo. Without it the page
 *   painted the Kaympus mark and then swapped — a two-brand flicker on every
 *   refresh. null means "main SaaS domain, or the school could not be
 *   resolved", i.e. show the Kaympus brand.
 */
export default function SignInPage({ initialBrand = null }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { showToast } = useToast();
  const [schoolCode, setSchoolCode] = useState(initialBrand?.schoolCode || '');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  // Set when the server ended the session on its own — an admin changed
  // this account's status or role. Landing on a blank login form with no
  // explanation reads as a bug; this says what happened.
  const [signOutReason, setSignOutReason] = useState('');
  useEffect(() => {
    setSignOutReason(takeSignOutReason());
  }, []);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [showLoginForm, setShowLoginForm] = useState(true);
  const [rememberMe, setRememberMe] = useState(true);
  const [isAutoDetected, setIsAutoDetected] = useState(!!initialBrand?.schoolCode);
  // Branding shown above the form: school's own logo + name when auto-detected,
  // Kaympus brand otherwise. Seeded from the server so there is no swap.
  const [schoolLogoUrl, setSchoolLogoUrl] = useState(initialBrand?.logoUrl || '');
  const [schoolName, setSchoolName] = useState(initialBrand?.schoolName || '');
  // Where the logo points. '/' until mounted — the tenant context is
  // window-only, and disagreeing with the server would hydration-mismatch the
  // very element the user is about to click.
  const [homeHref, setHomeHref] = useState('/');

  const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';

  useEffect(() => {
    setIsMounted(true);

    // Auto-detect tenant and pre-fill school code
    const detectTenant = async () => {
      try {
        const tenantContext = getCurrentTenantContext();
        debugLog('🔍 [SignInPage] Initial Tenant Context:', tenantContext);

        // The server already resolved this school, so its logo, name and code
        // are on screen from the first paint. Re-resolving here would only
        // repaint identical values (and burn a request) — the exact swap that
        // read as a flicker. Just settle the logo's destination and stop.
        if (initialBrand) {
          setHomeHref(getPreLoginHomeHref({ tenantResolved: true }));
          return;
        }

        // If we are on a subdomain/custom domain, try to resolve from backend
        if (['subdomain', 'customDomain'].includes(tenantContext?.mode)) {
          const { resolveTenant } = await import('@/utils/tenantResolver');
          debugLog('🔍 [SignInPage] Requesting resolution for:', tenantContext?.identifier);
          const resolved = await resolveTenant();
          // An unresolvable custom domain must not link to '/': the root route
          // redirect()s straight back to /domain-not-found, so the logo would
          // be a dead end. getPreLoginHomeHref sends it to the SaaS site.
          setHomeHref(getPreLoginHomeHref({ tenantResolved: !!resolved.resolved }));

          if (resolved.resolved) {
            debugLog('✅ [SignInPage] Tenant resolved successfully:', resolved.schoolCode);
            setSchoolCode(resolved.schoolCode || resolved.identifier);
            setSchoolLogoUrl(resolved.logoUrl || '');
            setSchoolName(resolved.schoolName || '');
            setIsAutoDetected(true);
          } else if (resolved.notFound) {
            // The backend answered clearly: there is NO school behind this
            // host. Prefilling the subdomain as a school code would send the
            // user into an OTP request that can only fail, on a login screen
            // branded for a school that doesn't exist. Fall back to the plain
            // form so they can type the real school code themselves.
            console.warn('⚠️ [SignInPage] No school for this host — showing manual school-code form');
            setSchoolCode('');
            setSchoolLogoUrl('');
            setSchoolName('');
            setIsAutoDetected(false);
          } else {
            console.warn('⚠️ [SignInPage] Backend resolution failed, using URL fallback:', tenantContext.identifier);

            // Fallback: the school may well exist — this was a network/5xx
            // failure, not a "no such school" answer — so keep the subdomain
            // as the code rather than making the user retype it.
            if (tenantContext.identifier && tenantContext.mode === 'subdomain') {
              setSchoolCode(tenantContext.identifier.toUpperCase());
              setIsAutoDetected(true);
            } else {
              console.warn('⚠️ [SignInPage] No valid tenant, but staying on page to avoid redirect loops.');
              setIsAutoDetected(false);
            }
          }
        } else if (tenantContext?.mode === 'development' && tenantContext?.identifier) {
          debugLog('🛠️ [SignInPage] Using Dev Mode identifier:', tenantContext.identifier);
          // Hit the backend so we can show the school's name + logo for branded sign-in
          // (without this, dev would auto-fill the school code but show the Kaympus fallback).
          const { resolveTenant } = await import('@/utils/tenantResolver');
          const resolved = await resolveTenant();
          if (resolved.resolved) {
            setSchoolCode(resolved.schoolCode || tenantContext.identifier);
            setSchoolLogoUrl(resolved.logoUrl || '');
            setSchoolName(resolved.schoolName || '');
            setIsAutoDetected(true);
          } else if (resolved.notFound) {
            // ?school=CODE naming a school that doesn't exist — same rule as
            // the subdomain branch: show the manual form instead of locking
            // the user to a dead code.
            setSchoolCode('');
            setIsAutoDetected(false);
          } else {
            setSchoolCode(tenantContext.identifier);
            setIsAutoDetected(true);
          }
          setHomeHref(getPreLoginHomeHref({ tenantResolved: true }));
        } else {
          debugLog('👤 [SignInPage] No tenant detected, showing manual login');
          setIsAutoDetected(false);
          setHomeHref(getPreLoginHomeHref({ tenantResolved: true }));
        }
      } catch (error) {
        console.error('❌ [SignInPage] Critical error in tenant detection:', error);
        setIsAutoDetected(false);
        setHomeHref(getPreLoginHomeHref({ tenantResolved: false }));
      }
    };

    detectTenant();

    // Check for session expiry error
    if (searchParams?.get('error') === 'session_expired') {
      showToast({
        message: 'Session expired. Please sign in again.',
        severity: 'warning',
      });
      // Clean up URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, '', newUrl);
    }

    // Load saved accounts
    const accounts = getSavedAccounts().filter(acc => acc.schoolCode !== 'SUPERADMIN');
    setSavedAccounts(accounts);
    if (accounts.length > 0) {
      setShowLoginForm(false);
    }
  }, [searchParams]);

  const handleSchoolCodeChange = (e) => {
    const value = e.target.value;
    const alphanumericValue = value.replace(/[^A-Za-z0-9]/g, '');
    setSchoolCode(alphanumericValue.toUpperCase());
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length <= 10) {
      setPhoneNumber(numericValue);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!schoolCode.trim()) {
      showToast({
        message: 'Please enter your school code',
        severity: 'error',
      });
      return;
    }

    if (!phoneNumber.trim() || phoneNumber.length !== 10) {
      showToast({
        message: 'Please enter a valid 10-digit phone number',
        severity: 'error',
      });
      return;
    }

    setIsLoading(true);

    const fetchUrl = `${API_BASE_URL}/api/auth/school/request-otp`;
    debugLog('🔑 [SignInPage] Sending OTP request to:', fetchUrl, { schoolCode, phone: phoneNumber });

    try {
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schoolCode: schoolCode.trim(),
          phone: phoneNumber.trim(),
        }),
      });

      debugLog('🔑 [SignInPage] Response Status:', response.status);

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        console.error('❌ [SignInPage] Request failed:', data);
        throw new Error(data.message || data.error?.message || 'We could not send the OTP right now. Please try again in a moment.');
      }

      const data = await response.json();

      if (typeof window !== 'undefined') {
        sessionStorage.setItem('phoneNumber', phoneNumber.trim());
        sessionStorage.setItem('schoolCode', schoolCode.trim());
        sessionStorage.setItem('otpContext', 'school');
        // Carry the brand forward. verify-otp and reset-password have no
        // tenant detection of their own; without this they would either show
        // the Kaympus mark on a school's sign-in journey, or have to repeat
        // the resolve and flicker exactly like this page used to.
        sessionStorage.setItem('brandLogoUrl', schoolLogoUrl || '');
        sessionStorage.setItem('brandSchoolName', schoolName || '');
        sessionStorage.setItem('brandTenantResolved', isAutoDetected ? '1' : '0');
        if (data.userRole) {
          sessionStorage.setItem('userRole', data.userRole);
        }
        // Lets the verify page run an expiry countdown matching the
        // server's OTP validity window instead of hardcoding it.
        if (data.expiresInSeconds) {
          sessionStorage.setItem('otpExpiresIn', String(data.expiresInSeconds));
        }

        // Save account if remember me is checked
        if (rememberMe) {
          saveAccount({
            schoolCode: schoolCode.trim(),
            phoneNumber: phoneNumber.trim(),
            userName: data.userName || null,
            userRole: data.userRole || null,
            photoUrl: data.photoUrl || null,
            associatedCount: data.associatedCount || 1,
          });
        }
      }

      showToast({
        message: 'OTP sent successfully! Check your phone.',
        severity: 'success',
      });

      // Carry the ?school= preview param forward. On a real subdomain or
      // custom domain the host alone identifies the school, but on localhost
      // (and on kaympus.com?school=CODE) the param IS the identity — drop it
      // and step two loses the school's branding.
      router.push(`/auth/verify-otp${schoolPreviewQuery()}`);
    } catch (error) {
      showToast({
        message: error.message || 'Failed to send OTP',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle quick login from saved account
  const handleSavedAccountLogin = async (account) => {
    setSchoolCode(account.schoolCode);
    setPhoneNumber(account.phoneNumber);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/school/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schoolCode: account.schoolCode,
          phone: account.phoneNumber,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || data.error?.message || 'We could not send the OTP right now. Please try again in a moment.');
      }

      const data = await response.json();

      if (typeof window !== 'undefined') {
        sessionStorage.setItem('phoneNumber', account.phoneNumber);
        sessionStorage.setItem('schoolCode', account.schoolCode);
        sessionStorage.setItem('otpContext', 'school');
        // Carry the brand forward. verify-otp and reset-password have no
        // tenant detection of their own; without this they would either show
        // the Kaympus mark on a school's sign-in journey, or have to repeat
        // the resolve and flicker exactly like this page used to.
        sessionStorage.setItem('brandLogoUrl', schoolLogoUrl || '');
        sessionStorage.setItem('brandSchoolName', schoolName || '');
        sessionStorage.setItem('brandTenantResolved', isAutoDetected ? '1' : '0');
        if (data.userRole) {
          sessionStorage.setItem('userRole', data.userRole);
        }
        if (data.expiresInSeconds) {
          sessionStorage.setItem('otpExpiresIn', String(data.expiresInSeconds));
        }

        // Update last used timestamp
        updateLastUsed(account.schoolCode, account.phoneNumber);
      }

      showToast({
        message: 'OTP sent successfully! Check your phone.',
        severity: 'success',
      });

      // Carry the ?school= preview param forward. On a real subdomain or
      // custom domain the host alone identifies the school, but on localhost
      // (and on kaympus.com?school=CODE) the param IS the identity — drop it
      // and step two loses the school's branding.
      router.push(`/auth/verify-otp${schoolPreviewQuery()}`);
    } catch (error) {
      showToast({
        message: error.message || 'Failed to send OTP',
        severity: 'error',
      });
      setIsLoading(false);
    }
  };

  // Handle removing a saved account
  const handleRemoveSavedAccount = (e, account) => {
    e.stopPropagation();
    removeSavedAccount(account.schoolCode, account.phoneNumber);
    const updatedAccounts = getSavedAccounts();
    setSavedAccounts(updatedAccounts);
    showToast({
      message: 'Account removed from saved list',
      severity: 'success',
    });
  };

  return (
    <div className="min-h-screen flex flex-col justify-between bg-gradient-to-br from-blue-50 via-white to-purple-50 px-5 py-6 sm:px-4 sm:py-8 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-blue-200/5 to-purple-200/5 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md mx-auto relative z-10 flex-grow flex flex-col justify-center animate-fade-in-up">
        {/* Logo: school's own when auto-detected, Kaympus brand otherwise.
            onError covers a school whose logoUrl is broken / 404.

            Links to the home of whichever brand is on screen — the school's
            public website on a school host, the Kaympus landing on ours. A
            plain <a>, not next/link: the root route is force-dynamic, so a
            soft nav stalls on an RSC round trip with no feedback, and a hard
            navigation re-evaluates the Host header, which is the whole point
            when crossing branding contexts. */}
        <div className="flex justify-center mb-8">
          <a
            href={homeHref}
            aria-label={schoolName ? `${schoolName} home` : 'Kaympus home'}
            title={schoolName ? `${schoolName} home` : 'Kaympus home'}
            className="inline-flex rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            data-testid="signin-logo-home-link"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={schoolLogoUrl || '/logo.png'}
              alt=""
              data-testid="signin-logo-img"
              className="h-16 sm:h-24 w-auto object-contain transform motion-safe:hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                if (e.currentTarget.src.indexOf('/logo.png') === -1) {
                  e.currentTarget.src = '/logo.png';
                }
              }}
            />
          </a>
        </div>

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent mb-2 sm:mb-3">
            Welcome Back
          </h1>
          <p className="text-gray-600 text-lg">
            Sign in to continue to your school
          </p>
        </div>

        {/* Why they are here, when they did not choose to be. */}
        {signOutReason ? (
          <div
            data-testid="sign-out-reason"
            className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            {signOutReason}
          </div>
        ) : null}

        {/* Saved Accounts Section */}
        {!showLoginForm && savedAccounts.length > 0 && (
          <div className="mb-6 px-0 sm:px-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Saved Accounts</h3>
            <div className="space-y-2">
              {savedAccounts.map((account, index) => (
                <div
                  key={index}
                  role="button"
                  onClick={() => handleSavedAccountLogin(account)}
                  className="w-full flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all duration-200 group cursor-pointer relative"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden">
                    {account.photoUrl ? (
                      // Keeps the gradient tile behind it: on failure the
                      // placeholder is transparent so the initials-style
                      // gradient shows through rather than a grey square.
                      <SafeImage
                        src={account.photoUrl}
                        alt={account.userName || 'User'}
                        variant="avatar"
                        name={account.userName || account.schoolCode}
                        placeholderClassName="bg-transparent text-white"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getInitials(account.userName || account.schoolCode)
                    )}
                  </div>

                  {/* Account Info */}
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate flex items-center gap-2">
                      {account.userName || `User ${account.phoneNumber.slice(-4)}`}
                      {account.associatedCount > 1 && (
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-medium border border-gray-200">
                          +{account.associatedCount - 1}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {account.schoolCode} • {account.phoneNumber}
                    </p>
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={(e) => handleRemoveSavedAccount(e, account)}
                    className="p-2 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors z-10 flex-shrink-0"
                    aria-label="Remove saved account"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Sign in with other account button */}
            <button
              type="button"
              onClick={() => setShowLoginForm(true)}
              className="w-full mt-4 py-3 px-4 bg-white border-2 border-dashed border-gray-300 rounded-xl text-gray-600 font-medium hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <User className="w-5 h-5" />
              Sign in with another account
            </button>
          </div>
        )}

        {/* Form Card with modern glassmorphism */}
        {(showLoginForm || savedAccounts.length === 0) && (
          <div className="relative group px-0 sm:px-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* School Code Input - Only visible on main domain with no detect param */}
              <div className="space-y-4">
                {/* 
                   Hidden when auto-detected (subdomain/dev-param). 
                   User is not allowed to change it.
                */}
                {!isAutoDetected && (
                  <div className="space-y-2 animate-in fade-in duration-300">
                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      School Code
                    </label>
                    <input
                      type="text"
                      value={schoolCode}
                      onChange={handleSchoolCodeChange}
                      placeholder="Enter school code"
                      className="w-full py-3 px-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 transition-all duration-300 hover:border-gray-300"
                      required
                    />
                  </div>
                )}
              </div>

              {/* Phone Number Input */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  autoFocus={isAutoDetected}
                  placeholder="Enter 10-digit phone number"
                  className="w-full py-3 px-4 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 transition-all duration-300 hover:border-gray-300"
                  required
                />
              </div>



              {/* Submit Button with gradient */}
              <button
                type="submit"
                disabled={isLoading}
                className="relative w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending OTP...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Continue
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                )}
              </button>
            </form>


          </div>
        )}
      </div>

      {/* Copyright */}
      <p className="text-center text-sm text-gray-500 mt-8 animate-fade-in-up delay-300 relative z-10">
        © {new Date().getFullYear()} Kaympus 
      </p>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
        }

        .delay-200 {
          animation-delay: 0.2s;
        }

        .delay-300 {
          animation-delay: 0.3s;
        }

        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}

